import {
  Button,
  FormControl,
  FormErrorMessage,
  Heading,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import React, { useState } from 'react';
import { TextField } from '../TextField';
import { getPasswordReset, getPasswordResetErrorMessage } from '../../api/User';
import { FormContainer } from '../FormContainer';
import * as yup from 'yup';
import { useSuccessToast } from '../hooks/toasters';

const initialFormValues = { email: '' };

type ForgotYourPasswordValues = typeof initialFormValues;

const forgotPasswordSchema = yup.object().shape({
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('An email is required')
    .max(120),
});

const useForgotYourPassword = () => {
  const [submissionError, setSubmissionError] = useState<string | undefined>();

  const toaster = useSuccessToast();

  const onSuccess = () =>
    toaster({
      title: 'Success',
      description: 'Please check your email inbox for a password reset link.',
    });

  const onSubmit = async (values: ForgotYourPasswordValues) => {
    try {
      let response = await getPasswordReset(values);

      if (response.status === 'ok') {
        onSuccess();
        setSubmissionError(undefined);
      } else if (response.status === 'error' && response.error) {
        setSubmissionError(getPasswordResetErrorMessage(response.error));
      }
    } catch (e) {
      console.log(e);
      setSubmissionError('Something went wrong. Please try again soon.');
    }
  };

  return { submissionError, onSubmit };
};

const ForgotYourPasswordForm = () => {
  const { submissionError, onSubmit } = useForgotYourPassword();

  return (
    <Formik
      initialValues={initialFormValues}
      validationSchema={forgotPasswordSchema}
      onSubmit={onSubmit}
    >
      {({ errors, touched, isSubmitting, isValid }) => (
        <Form>
          <Stack spacing={4} w="100%">
            <TextField name="email" label="Email" touched={touched.email} error={errors.email} />
            <FormControl isInvalid={submissionError !== undefined}>
              <FormErrorMessage>{submissionError}</FormErrorMessage>
            </FormControl>
          </Stack>
          <Button
            mt={4}
            isDisabled={!isValid}
            isLoading={isSubmitting}
            loadingText="Getting password reset"
            type="submit"
          >
            Get Password Reset
          </Button>
        </Form>
      )}
    </Formik>
  );
};

const ForgotYourPassword = () => (
  <FormContainer>
    <Heading as="h2" size="lg">
      Forgot your password?
    </Heading>
    <Text pt={2}>Enter your email to reset your password.</Text>
    <Spacer h={8} />
    <ForgotYourPasswordForm />
  </FormContainer>
);
export default ForgotYourPassword;
